import MonacoEditor from '@monaco-editor/react'
import { useCallback, useEffect, useState } from 'react'

import { compilePine } from '../../pine/index'
import { DEFAULT_PINE_CODE, usePineStore } from '../../stores/pineStore'
import type { PineCompileError, PineScript } from '../../stores/pineStore'
import { useThemeStore } from '../../stores/themeStore'

type UpdateFn = (
  id: string,
  patch: Partial<Pick<PineScript, 'title' | 'description' | 'source' | 'enabled'>>
) => PineCompileError[]

interface Props {
  script: PineScript | null
  onSaved: (sc: PineScript) => void
  updateScript: UpdateFn
}

export function PineScriptEditor({ script, onSaved, updateScript }: Props) {
  const { theme } = useThemeStore()
  const isDark = theme === 'dark'

  const [source, setSource] = useState(DEFAULT_PINE_CODE)
  const [errors, setErrors] = useState<string[]>([])

  useEffect(() => {
    setSource(script?.source ?? DEFAULT_PINE_CODE)
    setErrors([])
  }, [script?.id, script?.source])

  const handleSave = useCallback(() => {
    const result = compilePine(source)
    if (result.errors.length > 0) {
      setErrors(result.errors.map((e: { message: string }) => e.message))
      return
    }
    if (script) {
      const errs = updateScript(script.id, { source })
      if (errs.length > 0) {
        setErrors(errs.map((e) => e.message))
        return
      }
      const updated = usePineStore.getState().scripts.find((s) => s.id === script.id)
      if (updated) onSaved(updated)
    }
    setErrors([])
  }, [script, source, updateScript, onSaved])

  if (!script) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-tv-text-muted">
        <p className="text-sm">Select a script from the list or create a new one</p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-tv-border px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-tv-text-primary">{script.title}</span>
          <span className="rounded bg-tv-bg-secondary px-1.5 py-0.5 text-[10px] text-tv-text-muted">
            {script.overlay ? 'Overlay' : 'Sub-pane'}
          </span>
        </div>
        <button
          type="button"
          onClick={handleSave}
          className="bg-tv-accent hover:bg-tv-accent/80 rounded px-3 py-1 text-xs font-medium text-white"
        >
          Save &amp; Compile
        </button>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <MonacoEditor
          language="plaintext"
          theme={isDark ? 'vs-dark' : 'vs'}
          value={source}
          onChange={(val) => setSource(val ?? '')}
          options={{
            fontSize: 13,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            lineNumbers: 'on',
            tabSize: 4,
            insertSpaces: true,
          }}
        />
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="max-h-24 overflow-y-auto border-t border-tv-border bg-red-950/30 px-4 py-2">
          {errors.map((err, i) => (
            <p key={i} className="text-xs text-red-400">
              {err}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
